import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useParams } from "react-router-dom";
import CTABlock from "../../components/CTABlock";
import Store, { activeRouteKeys } from "../../Store/Store";
import useWasteLogsInfiniteScroll from "../../hooks/useWasteLogsInfiniteScroll";
import useWasteLogsSearch from "../../hooks/useWasteLogsSearch";
import SpecSpinner from "../../components/SpecSpinner";
import useEstablishmentSubscription from "../../hooks/useEstablishmentSubscription";
import SubscriptionScreenLock from "../../components/SubscriptionScreenLock";
import AddWasteLogModal from "../../components/Modals/AddWasteLogModal";
import WasteLogDeleteConfirmationModal from "../../components/Modals/AddWasteLogModal/WasteLogDeleteConfirmationModal";
import WasteLogDateGroup from "./components/WasteLogDateGroup";
import inventoryClient from "../../clients/inventoryClient";
import { useMutation, useQueryClient } from "react-query";

var moment = require("moment");

const WasteLog = () => {
  const store = useContext(Store);
  const { establishmentid } = useParams();
  const queryClient = useQueryClient();

  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    store.updateActiveRoute(
      activeRouteKeys.wasteLog,
      `/spec/${establishmentid}/wastelog`
    );
  }, []);

  // Debounce search text
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchText]);

  const { data: subscriptionState, isLoading: isLoadingSubscriptionState } =
    useEstablishmentSubscription(establishmentid);

  const isSearching = debouncedSearchText && debouncedSearchText.length >= 2;

  const {
    isLoading: isLoadingWasteLogs,
    data: wasteLogData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useWasteLogsInfiniteScroll(
    establishmentid,
    Boolean(subscriptionState?.isActive && !isSearching)
  );

  const {
    isLoading: isLoadingSearchResults,
    data: searchResultsData,
    fetchNextPage: fetchNextSearchPage,
    hasNextPage: hasNextSearchPage,
    isFetchingNextPage: isFetchingNextSearchPage,
  } = useWasteLogsSearch(
    establishmentid,
    debouncedSearchText,
    Boolean(subscriptionState?.isActive)
  );

  const deleteMutation = useMutation(
    (wasteLogId) =>
      inventoryClient.deleteWasteLog({
        establishmentId: establishmentid,
        wasteLogId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["waste-logs-infinite"]);
        queryClient.invalidateQueries(["waste-logs-search"]);
      },
    }
  );

  // Use search results if searching, otherwise use normal results
  const activeData = isSearching ? searchResultsData : wasteLogData;
  const activeHasNextPage = isSearching ? hasNextSearchPage : hasNextPage;
  const activeFetchNextPage = isSearching ? fetchNextSearchPage : fetchNextPage;
  const activeIsFetchingNextPage = isSearching ? isFetchingNextSearchPage : isFetchingNextPage;

  // Flatten all pages of waste logs
  const allWasteLogs = useMemo(() => {
    if (!activeData?.pages) return [];
    return activeData.pages.flatMap((page) => page.wasteLogs || []);
  }, [activeData]);

  // Group waste logs by date
  const groupedWasteLogs = useMemo(() => {
    const groups = {};
    allWasteLogs.forEach((log) => {
      const dateKey = moment(log.wasteDate).format("YYYY-MM-DD");
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(log);
    });

    // Sort by date descending and return as array of [date, logs] pairs
    return Object.entries(groups).sort((a, b) =>
      moment(b[0]).diff(moment(a[0]))
    );
  }, [allWasteLogs]);

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const isBottom = scrollHeight - scrollTop <= clientHeight + 100;
    if (isBottom && activeHasNextPage && !activeIsFetchingNextPage) {
      activeFetchNextPage();
    }
  };

  const handleOpenModal = () => {
    setEditingLog(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLog(null);
  };

  const handleEdit = (log) => {
    setEditingLog(log);
    setIsModalOpen(true);
  };

  const handleDelete = (log) => {
    setLogToDelete(log);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (logToDelete) {
      deleteMutation.mutate(logToDelete.id, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setLogToDelete(null);
        },
      });
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setLogToDelete(null);
  };

  // Only show full-screen spinner on initial load, not during search
  const showFullScreenSpinner = (isLoadingWasteLogs && !wasteLogData && !isSearching) || isLoadingSubscriptionState;

  if (showFullScreenSpinner) {
    return <SpecSpinner open text="Loading Waste Logs..." />;
  }

  const hasWasteLogs = allWasteLogs.length > 0;
  const isLoadingSearch = isSearching && isLoadingSearchResults && !searchResultsData;

  return (
    <>
      <Grid
        container
        sx={{
          maxWidth: "2440px",
          padding: 4,
          marginTop: 2,
        }}
      >
        {/* HEADER */}
        <Grid
          container
          sx={{
            maxWidth: "2440px",
            marginBottom: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Grid item xs={8}>
            <Typography variant="h2" sx={{ fontSize: { xs: 20, md: 28 } }}>
              Waste Log
            </Typography>
          </Grid>
          <Grid
            item
            xs={4}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                paddingX: 2,
                paddingY: 1,
                height: "40px",
              }}
              onClick={handleOpenModal}
            >
              <AddIcon sx={{ fontSize: { xs: 14, md: 20 } }} />
              <Typography
                variant="smallButton"
                sx={{ fontSize: { xs: 10, md: 14 } }}
              >
                Add New Log
              </Typography>
            </Button>
          </Grid>
        </Grid>

        {/* CONTENT */}
        <Box sx={{ width: "100%" }}>
          {hasWasteLogs || isSearching ? (
            <>
              {/* Search Bar */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  marginBottom: 4,
                  alignItems: "center",
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search waste logs..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{
                    flex: 1,
                    maxWidth: "400px",
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    "& .MuiOutlinedInput-root": {
                      background: (theme) => theme.palette.pure.white,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Waste Log List with Infinite Scroll */}
              {isLoadingSearch ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "300px",
                  }}
                >
                  <CircularProgress size={50} />
                </Box>
              ) : (
                <Box
                  sx={{
                    maxHeight: "calc(100vh - 300px)",
                    overflow: "auto",
                  }}
                  onScroll={handleScroll}
                >
                  {groupedWasteLogs.length > 0 ? (
                    <>
                      {groupedWasteLogs.map(([date, logs]) => (
                        <WasteLogDateGroup
                          key={date}
                          date={date}
                          logs={logs}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      ))}
                      {activeIsFetchingNextPage && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            padding: 4,
                          }}
                        >
                          <CircularProgress size={30} />
                        </Box>
                      )}
                      {!activeHasNextPage && groupedWasteLogs.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            padding: 4,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No more waste logs to load
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        padding: 8,
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No waste logs match your search
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </>
          ) : (
            <CTABlock
              header="Your Waste Log Is Empty..."
              subheader="You have not added any product formats to your waste log yet. Get started now..."
              buttonText="Add New Log"
              ButtonImage={AddIcon}
              handleCTA={handleOpenModal}
            />
          )}
        </Box>
      </Grid>

      <SubscriptionScreenLock show={!subscriptionState?.isActive} />

      {/* Add/Edit Waste Log Modal */}
      <AddWasteLogModal
        isOpen={isModalOpen}
        toggleModalOpen={handleCloseModal}
        existingWasteLog={editingLog}
      />

      {/* Delete Confirmation Modal */}
      <WasteLogDeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        wasteLog={logToDelete}
        isDeleting={deleteMutation.isLoading}
      />
    </>
  );
};

export default WasteLog;
